"use client"; // 클라이언트 컴포넌트로 지정

import { useEffect, useState } from "react";
import { IconButton, useTheme, Snackbar, Alert, AlertProps, Slide, SlideProps } from "@mui/material";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { useTranslations } from "next-intl";
import { safeT } from "@/utils/safeT";

// Props 타입 정의
interface ShareButtonProps {
  title?: string;
  text?: string;
}

const ShareButton = ({ title = document.title, text = "이 페이지를 확인해보세요!" }: ShareButtonProps) => {
  const [currentUrl, setCurrentUrl] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<AlertProps["severity"]>("success");
  const theme = useTheme();
  const t = useTranslations();
  // 클라이언트 측에서 현재 URL 가져오기
  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  // Snackbar 닫기 핸들러
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  // 공유 버튼 클릭 시 호출되는 함수
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url: currentUrl,
        });
        setMessage(
          safeT(t, "ui.message.shareSuccess", {
            defaultMessage: "공유 성공!",
          })
        );
        setSeverity("success");
        setOpen(true);
      } catch (error) {
        console.warn("공유 실패:", error);
        // setMessage("공유에 실패했습니다.");
        // setSeverity("error");
        // setOpen(true);
      }
    } else {
      // Web Share API를 지원하지 않는 경우 클립보드에 복사
      try {
        await navigator.clipboard.writeText(currentUrl);
        setMessage(
          safeT(t, "ui.message.copySuccess", {
            defaultMessage: "URL이 클립보드에 복사되었습니다!",
          })
        );
        setSeverity("success");
        setOpen(true);
      } catch (error) {
        console.warn("copy Failed:", error);
        setMessage(
          safeT(t, "ui.message.copyFailed", {
            defaultMessage: "URL 복사에 실패했습니다.",
          })
        );
        setSeverity("error");
        setOpen(true);
      }
    }
  };

  // Slide 애니메이션 설정
  const TransitionUp = (props: SlideProps) => {
    return <Slide {...props} direction="up" />;
  };

  return (
    <>
      <IconButton size="small" aria-label="share card" onClick={handleShare}>
        <ShareOutlinedIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
      </IconButton>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        slots={{ transition: TransitionUp }}
        sx={{
          width: "100%",
          maxWidth: theme.breakpoints.values.md,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
          position: "fixed",
          left: -8,
          right: 0,
          bottom: 0,
          margin: 0,
          padding: 0,
          zIndex: theme.zIndex.snackbar,
          "& .MuiSnackbarContent-root": {
            width: "100%",
            borderRadius: 0,
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={{
            width: "100%",
            height: "100%",
            minHeight: 100, // 높이 증가
            padding: "16px", // 패딩 조정
            borderTopLeftRadius: "18px", // 상단 라운드
            borderTopRightRadius: "18px",
            borderBottomLeftRadius: { xs: 0, sm: "12px" }, // 하단은 직선
            borderBottomRightRadius: { xs: 0, sm: "12px" },
            "& .MuiAlert-icon": {
              fontSize: 24,
            },
            "& .MuiAlert-message": {
              fontSize: "1rem",
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareButton;
